import React, { useState, useEffect, useCallback } from 'react';
import { withDataProvider, Title } from 'react-admin';
import { withRouter } from 'react-router-dom';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Chip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';

// Material UI Icons
import RefreshIcon from '@material-ui/icons/Refresh';
import FilterListIcon from '@material-ui/icons/FilterList';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet';
import PaymentIcon from '@material-ui/icons/Payment';
import WorkIcon from '@material-ui/icons/Work';
import VisibilityIcon from '@material-ui/icons/Visibility';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import SchoolIcon from '@material-ui/icons/School';
import BuildIcon from '@material-ui/icons/Build';
import GroupIcon from '@material-ui/icons/Group';
import AssignmentIcon from '@material-ui/icons/Assignment';
import BusinessIcon from '@material-ui/icons/Business';

const styles = (theme) => ({
  root: {
    padding: '10px 14px',
    backgroundColor: '#F8FAFC',
    minHeight: '100vh',
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    [theme.breakpoints.down('xs')]: {
      padding: '6px'
    }
  },
  headerBar: {
    padding: '10px 14px',
    marginBottom: 14,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    border: '1px solid #E2E8F0',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    [theme.breakpoints.down('xs')]: {
      padding: '8px',
      flexDirection: 'column',
      alignItems: 'stretch'
    }
  },
  headerTitle: {
    fontWeight: 800,
    color: '#0F172A',
    fontSize: '1.15rem',
    display: 'flex',
    alignItems: 'center',
    gap: 6
  },
  filterGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      justifyContent: 'space-between'
    }
  },
  formControl: {
    minWidth: 115,
    [theme.breakpoints.down('xs')]: {
      flex: 1,
      minWidth: 90
    }
  },
  selectInput: {
    fontSize: '0.8rem',
    padding: '5px 8px'
  },
  dateInput: {
    width: 155,
    '& .MuiInputBase-input': {
      fontSize: '0.8rem',
      padding: '6px 8px'
    }
  },
  kpiCard: {
    padding: '10px 12px',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    border: '1px solid #E2E8F0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.06)'
    },
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    minHeight: 85,
    width: '100%',
    boxSizing: 'border-box'
  },
  kpiHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4
  },
  kpiTitle: {
    fontSize: '0.7rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: '#64748B'
  },
  kpiIconBadge: {
    width: 28,
    height: 28,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  kpiValue: {
    fontSize: '1.3rem',
    fontWeight: 800,
    color: '#0F172A',
    lineHeight: 1.2
  },
  kpiSubtext: {
    fontSize: '0.7rem',
    color: '#64748B',
    marginTop: 2,
    display: 'flex',
    alignItems: 'center',
    gap: 4
  },
  sectionTitle: {
    fontSize: '0.9rem',
    fontWeight: 700,
    color: '#1E293B',
    marginBottom: 10,
    display: 'flex',
    alignItems: 'center',
    gap: 6
  },
  approvalBox: {
    padding: '16px 20px',
    borderRadius: 12,
    backgroundColor: '#FFFDF7',
    border: '1px solid #FDE68A',
    boxShadow: '0 4px 20px rgba(217, 119, 6, 0.07)',
    marginBottom: 18
  },
  approvalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 14,
    paddingBottom: 10,
    borderBottom: '1px solid #FEF3C7'
  },
  approvalItem: {
    padding: '14px 16px',
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    border: '1px solid #E2E8F0',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    minHeight: 125,
    height: '100%',
    boxSizing: 'border-box',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.09)'
    }
  },
  approvalItemTop: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 8
  },
  approvalIconBadge: {
    width: 36,
    height: 36,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
    flexShrink: 0
  },
  approvalItemBottom: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 10,
    paddingTop: 8,
    borderTop: '1px solid #F1F5F9'
  },
  approvalCountBadge: {
    padding: '2px 8px',
    borderRadius: 12,
    fontSize: '0.8rem',
    fontWeight: 800,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  approvalTypeBadge: {
    fontSize: '0.625rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: '2px 6px',
    borderRadius: 4,
    display: 'inline-block',
    marginBottom: 4
  },
  approvalActionBtn: {
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '0.725rem',
    padding: '4px 12px',
    borderRadius: 6,
    boxShadow: 'none',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.18)'
    }
  },
  serviceCard: {
    padding: '12px 14px',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    border: '1px solid #E2E8F0',
    height: '100%',
    boxSizing: 'border-box'
  },
  tablePaper: {
    borderRadius: 8,
    border: '1px solid #E2E8F0',
    overflow: 'hidden',
    backgroundColor: '#FFFFFF'
  },
  tableContainerResponsive: {
    overflowX: 'auto',
    width: '100%'
  },
  tableHeaderCell: {
    backgroundColor: '#F1F5F9',
    fontWeight: 800,
    color: '#334155',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    padding: '8px 12px',
    whiteSpace: 'nowrap',
    letterSpacing: '0.4px'
  },
  tableCell: {
    fontSize: '0.8rem',
    padding: '8px 12px',
    whiteSpace: 'nowrap'
  },
  chartContainer: {
    padding: '12px 14px',
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    border: '1px solid #E2E8F0',
    boxSizing: 'border-box'
  },
  chartBarArea: {
    display: 'flex',
    alignItems: 'flex-end',
    height: 130,
    gap: 16,
    paddingTop: 12,
    paddingBottom: 6,
    borderBottom: '1px solid #E2E8F0',
    overflowX: 'auto',
    justifyContent: 'space-around'
  },
  chartColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-end',
    minWidth: 36,
    height: '100%'
  },
  chartBarGroup: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 4,
    height: 95,
    width: '100%',
    justifyContent: 'center'
  },
  chartBarGMV: {
    width: 14,
    backgroundColor: '#0284C7',
    borderRadius: '3px 3px 0 0',
    transition: 'height 0.3s ease',
    '&:hover': {
      backgroundColor: '#0369A1'
    }
  },
  chartBarRev: {
    width: 14,
    backgroundColor: '#6366F1',
    borderRadius: '3px 3px 0 0',
    transition: 'height 0.3s ease',
    '&:hover': {
      backgroundColor: '#4338CA'
    }
  },
  chartDateLabel: {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#64748B',
    marginTop: 6,
    whiteSpace: 'nowrap'
  }
});

const AdminDashboard = (props) => {
  const { classes, dataProvider, history } = props;

  const [loading, setLoading] = useState(true);
  const [datePreset, setDatePreset] = useState('this_month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');

  const [dashData, setDashData] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  // Compute preset dates
  const applyPreset = (preset) => {
    setDatePreset(preset);
    const now = new Date();
    let start = new Date();
    let end = new Date();

    if (preset === 'today') {
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
    } else if (preset === 'yesterday') {
      start.setDate(now.getDate() - 1);
      start.setHours(0, 0, 0, 0);
      end.setDate(now.getDate() - 1);
      end.setHours(23, 59, 59, 999);
    } else if (preset === 'last_7_days') {
      start.setDate(now.getDate() - 7);
    } else if (preset === 'last_30_days') {
      start.setDate(now.getDate() - 30);
    } else if (preset === 'this_month') {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
    } else if (preset === 'last_month') {
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
    } else if (preset === 'this_year') {
      start = new Date(now.getFullYear(), 0, 1);
    } else if (preset === 'last_year') {
      start = new Date(now.getFullYear() - 1, 0, 1);
      end = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
    } else if (preset === 'custom') {
      return;
    }

    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const filter = {
        startDate,
        endDate,
        service: serviceFilter,
        paymentMethod: paymentMethodFilter
      };
      const res = await dataProvider('GET_LIST', 'dashboard', { filter });
      if (res && res.data) {
        setDashData(res.data);
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, [dataProvider, startDate, endDate, serviceFilter, paymentMethodFilter]);

  useEffect(() => {
    applyPreset('this_month');
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleResetFilters = () => {
    setDatePreset('this_month');
    setServiceFilter('all');
    setPaymentMethodFilter('all');
    applyPreset('this_month');
  };

  const financials = dashData?.financials || {
    gmv: 0,
    paymentsReceived: 0,
    pendingCashAmount: 0,
    outstandingBalance: 0,
    platformRevenue: 0,
    vendorEarnings: 0
  };

  const pendingActions = dashData?.pendingActions || {
    pendingCashApprovals: 0,
    pendingCashAmount: 0,
    pendingVendors: 0,
    pendingVendorsAwaiting: 0,
    pendingVendorsDisqualified: 0,
    pendingVendorServices: 0,
    pendingTrainingServices: 0,
    pendingManpowerServices: 0,
    pendingProjectServices: 0,
    pendingRentalServices: 0,
    pendingCourses: 0,
    pendingTools: 0,
    totalPendingActions: 0
  };

  const servicePerf = dashData?.servicePerformance || {
    manpower: { totalRequests: 0, activeJobs: 0, completedJobs: 0, gmv: 0, paymentsReceived: 0, outstandingBalance: 0, platformRevenue: 0, pendingVendors: 0 },
    project: { totalRequests: 0, activeJobs: 0, completedJobs: 0, gmv: 0, paymentsReceived: 0, outstandingBalance: 0, platformRevenue: 0, pendingVendors: 0 },
    rental: { totalRequests: 0, activeJobs: 0, completedJobs: 0, gmv: 0, paymentsReceived: 0, outstandingBalance: 0, platformRevenue: 0, pendingListings: 0, approvedListings: 0, pendingVendors: 0 },
    training: { totalRequests: 0, activeJobs: 0, completedJobs: 0, gmv: 0, paymentsReceived: 0, outstandingBalance: 0, platformRevenue: 0, pendingCourses: 0, approvedCourses: 0, pendingTrainers: 0 }
  };

  // Safe metric extraction helper
  const getVal = (obj, ...keys) => {
    if (!obj) return 0;
    for (const k of keys) {
      if (obj[k] !== undefined && obj[k] !== null) {
        return typeof obj[k] === 'number' ? obj[k] : (parseFloat(obj[k]) || 0);
      }
    }
    return 0;
  };
  const fmtVal = (val) => (val || 0).toLocaleString('en-IN');

  // Dynamic service breakdown for pending cash payment approvals
  const cashPendingByService = {};
  const pendingList = dashData?.pendingActions?.pendingCashApprovalsList || [];
  if (pendingList.length > 0) {
    pendingList.forEach((item) => {
      const svc = (item.serviceType || 'manpower').toLowerCase();
      const title = item.serviceTitle || (svc.charAt(0).toUpperCase() + svc.slice(1));
      if (!cashPendingByService[svc]) {
        cashPendingByService[svc] = { serviceType: svc, serviceTitle: title, count: 0, totalAmount: 0 };
      }
      cashPendingByService[svc].count++;
      cashPendingByService[svc].totalAmount += (parseFloat(item.amountRequested) || 0);
    });
  } else if (pendingActions.pendingCashApprovals > 0) {
    ['manpower', 'project', 'rental', 'training'].forEach((svc) => {
      const perf = servicePerf[svc];
      if (perf && perf.pendingCash > 0) {
        const title = svc.charAt(0).toUpperCase() + svc.slice(1);
        cashPendingByService[svc] = { serviceType: svc, serviceTitle: title, count: 1, totalAmount: perf.pendingCash };
      }
    });
    if (Object.keys(cashPendingByService).length === 0) {
      cashPendingByService['manpower'] = { serviceType: 'manpower', serviceTitle: 'Manpower', count: pendingActions.pendingCashApprovals, totalAmount: pendingActions.pendingCashAmount };
    }
  }

  const revenueTrend = dashData?.revenueTrend || [];
  const maxTrendVal = Math.max(1, ...revenueTrend.map((t) => Math.max(t.gmv || 0, t.revenue || 0)));

  // Helper for status badges (UPPERCASE with custom color rules)
  const getStatusBadge = (status) => {
    const st = (status || 'PENDING').toUpperCase();
    let bg = '#F1F5F9';
    let color = '#334155';
    if (st.includes('HISTORY') || st.includes('COMPLETE')) {
      bg = '#DCFCE7';
      color = '#15803D';
    } else if (st.includes('PENDING') || st.includes('APPLIED')) {
      bg = '#FEF3C7';
      color = '#B45309';
    } else if (st.includes('APPROV')) {
      bg = '#E0F2FE';
      color = '#0369A1';
    } else if (st.includes('REJECT') || st.includes('CANCEL')) {
      bg = '#FEE2E2';
      color = '#B91C1C';
    } else if (st.includes('TRAINING') || st.includes('MANPOWER') || st.includes('RENTAL') || st.includes('PROJECT')) {
      bg = '#EEF2FF';
      color = '#4338CA';
    }
    return (
      <Chip
        label={st}
        size="small"
        style={{
          backgroundColor: bg,
          color: color,
          fontWeight: 800,
          fontSize: '0.675rem',
          height: 20,
          letterSpacing: '0.4px'
        }}
      />
    );
  };

  return (
    <div className={classes.root}>
      <Title title="Admin Dashboard - TVH" />

      {/* 1. Header Bar & Date Filters (Date Overflow Fixed) */}
      <Paper className={classes.headerBar}>
        <Box>
          <Typography className={classes.headerTitle}>
            <WorkIcon style={{ color: '#0284C7', fontSize: 20 }} /> Operations BI Dashboard
          </Typography>
        </Box>

        <Box className={classes.filterGroup}>
          <FormControl className={classes.formControl} variant="outlined" size="small">
            <InputLabel style={{ fontSize: '0.75rem' }}>Date Range</InputLabel>
            <Select
              value={datePreset}
              onChange={(e) => applyPreset(e.target.value)}
              label="Date Range"
              className={classes.selectInput}
            >
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="yesterday">Yesterday</MenuItem>
              <MenuItem value="last_7_days">Last 7 Days</MenuItem>
              <MenuItem value="last_30_days">Last 30 Days</MenuItem>
              <MenuItem value="this_month">This Month</MenuItem>
              <MenuItem value="last_month">Last Month</MenuItem>
              <MenuItem value="this_year">This Year</MenuItem>
              <MenuItem value="last_year">Last Year</MenuItem>
              <MenuItem value="custom">Custom Range</MenuItem>
            </Select>
          </FormControl>

          {datePreset === 'custom' && (
            <>
              <TextField
                type="date"
                label="Start Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
                className={classes.dateInput}
              />
              <TextField
                type="date"
                label="End Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
                className={classes.dateInput}
              />
            </>
          )}

          <FormControl className={classes.formControl} variant="outlined" size="small">
            <InputLabel style={{ fontSize: '0.75rem' }}>Service</InputLabel>
            <Select
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              label="Service"
              className={classes.selectInput}
            >
              <MenuItem value="all">All Services</MenuItem>
              <MenuItem value="manpower">Manpower</MenuItem>
              <MenuItem value="project">Project</MenuItem>
              <MenuItem value="training">Training</MenuItem>
              <MenuItem value="rental">Rental</MenuItem>
            </Select>
          </FormControl>

          <FormControl className={classes.formControl} variant="outlined" size="small">
            <InputLabel style={{ fontSize: '0.75rem' }}>Payment</InputLabel>
            <Select
              value={paymentMethodFilter}
              onChange={(e) => setPaymentMethodFilter(e.target.value)}
              label="Payment"
              className={classes.selectInput}
            >
              <MenuItem value="all">All Methods</MenuItem>
              <MenuItem value="online">Online</MenuItem>
              <MenuItem value="cash">Cash</MenuItem>
            </Select>
          </FormControl>

          <Button
            size="small"
            variant="outlined"
            onClick={handleResetFilters}
            startIcon={<FilterListIcon style={{ fontSize: 14 }} />}
            style={{ textTransform: 'none', fontSize: '0.75rem', padding: '4px 10px' }}
          >
            Reset
          </Button>
          <IconButton size="small" color="primary" onClick={fetchDashboardData} disabled={loading} style={{ padding: 4 }}>
            {loading ? <CircularProgress size={18} /> : <RefreshIcon style={{ fontSize: 20 }} />}
          </IconButton>
        </Box>
      </Paper>

      {/* 2. Top-Level KPI Cards (4 Cards per Row, 8 Cards Total Across 2 Rows) */}
      <Grid container spacing={2} style={{ marginBottom: 14 }}>
        {/* Row 1: Total & Completed Requests | Active Jobs | Pending Approvals | Accepted Value (GMV) */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.kpiCard} style={{ borderLeft: '4px solid #0284C7' }}>
            <Box className={classes.kpiHeader}>
              <Typography className={classes.kpiTitle}>Total / Completed Requests</Typography>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {loading && <CircularProgress size={14} style={{ color: '#0284C7' }} />}
                <Box className={classes.kpiIconBadge} style={{ backgroundColor: '#E0F2FE', color: '#0284C7' }}>
                  <WorkIcon style={{ fontSize: 16 }} />
                </Box>
              </Box>
            </Box>
            <Box style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <Typography className={classes.kpiValue}>{(dashData?.totalRequests || 0).toLocaleString('en-IN')}</Typography>
              <Typography style={{ fontSize: '0.75rem', color: '#64748B', fontWeight: 600 }}>Total</Typography>
            </Box>
            <Typography className={classes.kpiSubtext}>
              <span style={{ color: '#16A34A', fontWeight: 700 }}>{(dashData?.completedJobs || 0).toLocaleString('en-IN')} Completed</span> • {(dashData?.activeJobs || 0).toLocaleString('en-IN')} Active
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.kpiCard} style={{ borderLeft: '4px solid #D97706' }}>
            <Box className={classes.kpiHeader}>
              <Typography className={classes.kpiTitle}>Active Jobs</Typography>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {loading && <CircularProgress size={14} style={{ color: '#D97706' }} />}
                <Box className={classes.kpiIconBadge} style={{ backgroundColor: '#FEF3C7', color: '#D97706' }}>
                  <HourglassEmptyIcon style={{ fontSize: 16 }} />
                </Box>
              </Box>
            </Box>
            <Typography className={classes.kpiValue}>{(dashData?.activeJobs || 0).toLocaleString('en-IN')}</Typography>
            <Typography className={classes.kpiSubtext}> In Progress Operations</Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.kpiCard} style={{ borderLeft: '4px solid #DC2626' }}>
            <Box className={classes.kpiHeader}>
              <Typography className={classes.kpiTitle}>Pending Approvals</Typography>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {loading && <CircularProgress size={14} style={{ color: '#DC2626' }} />}
                <Tooltip title={`Cash: ${pendingActions.pendingCashApprovals} | New Vendors: ${pendingActions.pendingNewVendors || pendingActions.pendingVendorsAwaiting || 0} (Disqualified: ${pendingActions.pendingVendorsDisqualified || 0}) | Training: ${pendingActions.pendingTrainingServices || 0} | Manpower: ${pendingActions.pendingManpowerServices || 0} | Projects: ${pendingActions.pendingProjectServices || 0} | Rentals: ${pendingActions.pendingRentalServices || 0} | Courses: ${pendingActions.pendingCourses} | Tools: ${pendingActions.pendingTools}`}>
                  <Box className={classes.kpiIconBadge} style={{ backgroundColor: '#FEE2E2', color: '#DC2626', cursor: 'pointer' }}>
                    <WarningIcon style={{ fontSize: 16 }} />
                  </Box>
                </Tooltip>
              </Box>
            </Box>
            <Typography className={classes.kpiValue} style={{ color: '#DC2626' }}>
              {pendingActions.totalPendingActions}
            </Typography>
            <Typography className={classes.kpiSubtext} style={{ fontSize: '0.675rem', color: '#7F1D1D', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              New: <b>{pendingActions.pendingNewVendors || pendingActions.pendingVendorsAwaiting || 0}</b> • Training: <b>{pendingActions.pendingTrainingServices || 0}</b> • MP: <b>{pendingActions.pendingManpowerServices || 0}</b> • PR: <b>{pendingActions.pendingProjectServices || 0}</b> • RN: <b>{pendingActions.pendingRentalServices || 0}</b>
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.kpiCard} style={{ borderLeft: '4px solid #2563EB' }}>
            <Box className={classes.kpiHeader}>
              <Typography className={classes.kpiTitle}>Accepted Value (GMV)</Typography>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {loading && <CircularProgress size={14} style={{ color: '#2563EB' }} />}
                <Box className={classes.kpiIconBadge} style={{ backgroundColor: '#DBEAFE', color: '#2563EB' }}>
                  <AttachMoneyIcon style={{ fontSize: 16 }} />
                </Box>
              </Box>
            </Box>
            <Typography className={classes.kpiValue}>₹{financials.gmv.toLocaleString('en-IN')}</Typography>
            <Typography className={classes.kpiSubtext}> Agreed Quotation Total</Typography>
          </Paper>
        </Grid>

        {/* Row 2: Payments Received | Pending Cash Receipt | Outstanding Balance | Platform Revenue */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.kpiCard} style={{ borderLeft: '4px solid #059669' }}>
            <Box className={classes.kpiHeader}>
              <Typography className={classes.kpiTitle}>Payments Received</Typography>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {loading && <CircularProgress size={14} style={{ color: '#059669' }} />}
                <Box className={classes.kpiIconBadge} style={{ backgroundColor: '#D1FAE5', color: '#059669' }}>
                  <PaymentIcon style={{ fontSize: 16 }} />
                </Box>
              </Box>
            </Box>
            <Typography className={classes.kpiValue} style={{ color: '#059669' }}>
              ₹{financials.paymentsReceived.toLocaleString('en-IN')}
            </Typography>
            <Typography className={classes.kpiSubtext}> Confirmed Collections</Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.kpiCard} style={{ borderLeft: '4px solid #EA580C' }}>
            <Box className={classes.kpiHeader}>
              <Typography className={classes.kpiTitle}>Pending Cash Receipt</Typography>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {loading && <CircularProgress size={14} style={{ color: '#EA580C' }} />}
                <Box className={classes.kpiIconBadge} style={{ backgroundColor: '#FFEDD5', color: '#EA580C' }}>
                  <AccountBalanceWalletIcon style={{ fontSize: 16 }} />
                </Box>
              </Box>
            </Box>
            <Typography className={classes.kpiValue} style={{ color: '#EA580C' }}>
              ₹{financials.pendingCashAmount.toLocaleString('en-IN')}
            </Typography>
            <Typography className={classes.kpiSubtext}>{pendingActions.pendingCashApprovals} Cash Approvals Due</Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.kpiCard} style={{ borderLeft: '4px solid #B45309' }}>
            <Box className={classes.kpiHeader}>
              <Typography className={classes.kpiTitle}>Outstanding Balance</Typography>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {loading && <CircularProgress size={14} style={{ color: '#B45309' }} />}
                <Box className={classes.kpiIconBadge} style={{ backgroundColor: '#FEF3C7', color: '#B45309' }}>
                  <HourglassEmptyIcon style={{ fontSize: 16 }} />
                </Box>
              </Box>
            </Box>
            <Typography className={classes.kpiValue} style={{ color: '#B45309' }}>
              ₹{financials.outstandingBalance.toLocaleString('en-IN')}
            </Typography>
            <Typography className={classes.kpiSubtext}> Customer Balance Due</Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.kpiCard} style={{ borderLeft: '4px solid #4F46E5' }}>
            <Box className={classes.kpiHeader}>
              <Typography className={classes.kpiTitle}>Platform Revenue</Typography>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {loading && <CircularProgress size={14} style={{ color: '#4F46E5' }} />}
                <Box className={classes.kpiIconBadge} style={{ backgroundColor: '#EEF2FF', color: '#4F46E5' }}>
                  <TrendingUpIcon style={{ fontSize: 16 }} />
                </Box>
              </Box>
            </Box>
            <Typography className={classes.kpiValue} style={{ color: '#4F46E5' }}>
              ₹{financials.platformRevenue.toLocaleString('en-IN')}
            </Typography>
            <Typography className={classes.kpiSubtext}> Net Platform Commission</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* 3. Actionable Admin Approval Center Banner */}
      {pendingActions.totalPendingActions > 0 && (
        <Box className={classes.approvalBox}>
          <Box className={classes.approvalHeader}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Box style={{ width: 34, height: 34, borderRadius: 8, backgroundColor: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <WarningIcon style={{ color: '#D97706', fontSize: 20 }} />
              </Box>
              <Box>
                <Typography style={{ fontWeight: 800, fontSize: '0.95rem', color: '#92400E' }}>
                  Actionable Admin Approval Center ({pendingActions.totalPendingActions} Pending Items)
                </Typography>
                <Typography style={{ fontSize: '0.725rem', color: '#B45309' }}>
                  Awaiting administrative review across vendor onboarding, service roles, and platform transactions
                </Typography>
              </Box>
            </Box>
            {loading && <CircularProgress size={16} style={{ color: '#D97706' }} />}
          </Box>

          <Grid container spacing={2}>
            {/* New Vendor Applications */}
            {(pendingActions.pendingNewVendors !== undefined ? pendingActions.pendingNewVendors : (pendingActions.pendingVendorsAwaiting !== undefined ? pendingActions.pendingVendorsAwaiting : pendingActions.pendingVendors)) > 0 && (
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Box className={classes.approvalItem} style={{ borderLeft: '4px solid #EA580C' }}>
                  <Box>
                    <Box className={classes.approvalItemTop}>
                      <Box style={{ display: 'flex', alignItems: 'center' }}>
                        <Box className={classes.approvalIconBadge} style={{ backgroundColor: '#FFEDD5', color: '#EA580C' }}>
                          <PersonAddIcon style={{ fontSize: 20 }} />
                        </Box>
                        <Box>
                          <span className={classes.approvalTypeBadge} style={{ backgroundColor: '#FFEDD5', color: '#C2410C' }}>
                            Onboarding
                          </span>
                          <Typography style={{ fontWeight: 800, fontSize: '0.85rem', color: '#1E293B', lineHeight: 1.2 }}>
                            New Vendor Applications
                          </Typography>
                        </Box>
                      </Box>
                      <span className={classes.approvalCountBadge} style={{ backgroundColor: '#FFF7ED', color: '#EA580C', border: '1px solid #FDBA74' }}>
                        {pendingActions.pendingNewVendors !== undefined ? pendingActions.pendingNewVendors : (pendingActions.pendingVendorsAwaiting || 0)}
                      </span>
                    </Box>
                    <Typography style={{ fontSize: '0.725rem', color: '#64748B', marginTop: 6, lineHeight: 1.4 }}>
                      Initial vendor registrations with no active services yet.
                      {pendingActions.pendingVendorsDisqualified ? ` (${pendingActions.pendingVendorsDisqualified} disqualified)` : ''}
                    </Typography>
                  </Box>
                  <Box className={classes.approvalItemBottom}>
                    <Typography style={{ fontSize: '0.7rem', fontWeight: 600, color: '#EA580C' }}>
                      {pendingActions.pendingNewVendors !== undefined ? pendingActions.pendingNewVendors : (pendingActions.pendingVendorsAwaiting || 0)} Awaiting Review
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      className={classes.approvalActionBtn}
                      style={{ backgroundColor: '#EA580C', color: '#FFF' }}
                      onClick={() => history.push('/vendors?filter=' + encodeURIComponent(JSON.stringify({ newVendor: true })))}
                    >
                      Review
                    </Button>
                  </Box>
                </Box>
              </Grid>
            )}

            {/* Training Service Role Approvals */}
            {pendingActions.pendingTrainingServices > 0 && (
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Box className={classes.approvalItem} style={{ borderLeft: '4px solid #7C3AED' }}>
                  <Box>
                    <Box className={classes.approvalItemTop}>
                      <Box style={{ display: 'flex', alignItems: 'center' }}>
                        <Box className={classes.approvalIconBadge} style={{ backgroundColor: '#EDE9FE', color: '#7C3AED' }}>
                          <SchoolIcon style={{ fontSize: 20 }} />
                        </Box>
                        <Box>
                          <span className={classes.approvalTypeBadge} style={{ backgroundColor: '#EDE9FE', color: '#6D28D9' }}>
                            Trainer Role
                          </span>
                          <Typography style={{ fontWeight: 800, fontSize: '0.85rem', color: '#1E293B', lineHeight: 1.2 }}>
                            Training Approvals
                          </Typography>
                        </Box>
                      </Box>
                      <span className={classes.approvalCountBadge} style={{ backgroundColor: '#F5F3FF', color: '#7C3AED', border: '1px solid #DDD6FE' }}>
                        {pendingActions.pendingTrainingServices}
                      </span>
                    </Box>
                    <Typography style={{ fontSize: '0.725rem', color: '#64748B', marginTop: 6, lineHeight: 1.4 }}>
                      Active vendors requesting Trainer role authorization to host training sessions.
                    </Typography>
                  </Box>
                  <Box className={classes.approvalItemBottom}>
                    <Typography style={{ fontSize: '0.7rem', fontWeight: 600, color: '#7C3AED' }}>
                      {pendingActions.pendingTrainingServices} Trainer Requests
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      className={classes.approvalActionBtn}
                      style={{ backgroundColor: '#7C3AED', color: '#FFF' }}
                      onClick={() => history.push('/vendors?filter=' + encodeURIComponent(JSON.stringify({ pendingService: 'training', trainerStatus: 'request_pending' })))}
                    >
                      Review
                    </Button>
                  </Box>
                </Box>
              </Grid>
            )}

            {/* Manpower Service Role Approvals */}
            {pendingActions.pendingManpowerServices > 0 && (
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Box className={classes.approvalItem} style={{ borderLeft: '4px solid #0284C7' }}>
                  <Box>
                    <Box className={classes.approvalItemTop}>
                      <Box style={{ display: 'flex', alignItems: 'center' }}>
                        <Box className={classes.approvalIconBadge} style={{ backgroundColor: '#E0F2FE', color: '#0284C7' }}>
                          <GroupIcon style={{ fontSize: 20 }} />
                        </Box>
                        <Box>
                          <span className={classes.approvalTypeBadge} style={{ backgroundColor: '#E0F2FE', color: '#0369A1' }}>
                            Manpower Role
                          </span>
                          <Typography style={{ fontWeight: 800, fontSize: '0.85rem', color: '#1E293B', lineHeight: 1.2 }}>
                            Manpower Approvals
                          </Typography>
                        </Box>
                      </Box>
                      <span className={classes.approvalCountBadge} style={{ backgroundColor: '#F0F9FF', color: '#0284C7', border: '1px solid #BAE6FD' }}>
                        {pendingActions.pendingManpowerServices}
                      </span>
                    </Box>
                    <Typography style={{ fontSize: '0.725rem', color: '#64748B', marginTop: 6, lineHeight: 1.4 }}>
                      Active vendors requesting Manpower supplier authorization for workforce supply.
                    </Typography>
                  </Box>
                  <Box className={classes.approvalItemBottom}>
                    <Typography style={{ fontSize: '0.7rem', fontWeight: 600, color: '#0284C7' }}>
                      {pendingActions.pendingManpowerServices} Supplier Requests
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      className={classes.approvalActionBtn}
                      style={{ backgroundColor: '#0284C7', color: '#FFF' }}
                      onClick={() => history.push('/vendors?filter=' + encodeURIComponent(JSON.stringify({ pendingService: 'manpower', manpowerStatus: 'request_pending' })))}
                    >
                      Review
                    </Button>
                  </Box>
                </Box>
              </Grid>
            )}

            {/* Project Service Role Approvals */}
            {pendingActions.pendingProjectServices > 0 && (
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Box className={classes.approvalItem} style={{ borderLeft: '4px solid #D97706' }}>
                  <Box>
                    <Box className={classes.approvalItemTop}>
                      <Box style={{ display: 'flex', alignItems: 'center' }}>
                        <Box className={classes.approvalIconBadge} style={{ backgroundColor: '#FEF3C7', color: '#D97706' }}>
                          <AssignmentIcon style={{ fontSize: 20 }} />
                        </Box>
                        <Box>
                          <span className={classes.approvalTypeBadge} style={{ backgroundColor: '#FEF3C7', color: '#B45309' }}>
                            Project Role
                          </span>
                          <Typography style={{ fontWeight: 800, fontSize: '0.85rem', color: '#1E293B', lineHeight: 1.2 }}>
                            Project Approvals
                          </Typography>
                        </Box>
                      </Box>
                      <span className={classes.approvalCountBadge} style={{ backgroundColor: '#FFFDF5', color: '#D97706', border: '1px solid #FDE68A' }}>
                        {pendingActions.pendingProjectServices}
                      </span>
                    </Box>
                    <Typography style={{ fontSize: '0.725rem', color: '#64748B', marginTop: 6, lineHeight: 1.4 }}>
                      Active vendors requesting Project contractor authorization for turnkey projects.
                    </Typography>
                  </Box>
                  <Box className={classes.approvalItemBottom}>
                    <Typography style={{ fontSize: '0.7rem', fontWeight: 600, color: '#D97706' }}>
                      {pendingActions.pendingProjectServices} Contractor Requests
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      className={classes.approvalActionBtn}
                      style={{ backgroundColor: '#D97706', color: '#FFF' }}
                      onClick={() => history.push('/vendors?filter=' + encodeURIComponent(JSON.stringify({ pendingService: 'project', projectStatus: 'request_pending' })))}
                    >
                      Review
                    </Button>
                  </Box>
                </Box>
              </Grid>
            )}

            {/* Rental Service Role Approvals */}
            {pendingActions.pendingRentalServices > 0 && (
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Box className={classes.approvalItem} style={{ borderLeft: '4px solid #0D9488' }}>
                  <Box>
                    <Box className={classes.approvalItemTop}>
                      <Box style={{ display: 'flex', alignItems: 'center' }}>
                        <Box className={classes.approvalIconBadge} style={{ backgroundColor: '#CCFBF1', color: '#0D9488' }}>
                          <BuildIcon style={{ fontSize: 20 }} />
                        </Box>
                        <Box>
                          <span className={classes.approvalTypeBadge} style={{ backgroundColor: '#CCFBF1', color: '#0F766E' }}>
                            Rental Role
                          </span>
                          <Typography style={{ fontWeight: 800, fontSize: '0.85rem', color: '#1E293B', lineHeight: 1.2 }}>
                            Rental Approvals
                          </Typography>
                        </Box>
                      </Box>
                      <span className={classes.approvalCountBadge} style={{ backgroundColor: '#F0FDFA', color: '#0D9488', border: '1px solid #99F6E4' }}>
                        {pendingActions.pendingRentalServices}
                      </span>
                    </Box>
                    <Typography style={{ fontSize: '0.725rem', color: '#64748B', marginTop: 6, lineHeight: 1.4 }}>
                      Active vendors requesting Machinery & Equipment supplier authorization.
                    </Typography>
                  </Box>
                  <Box className={classes.approvalItemBottom}>
                    <Typography style={{ fontSize: '0.7rem', fontWeight: 600, color: '#0D9488' }}>
                      {pendingActions.pendingRentalServices} Equipment Requests
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      className={classes.approvalActionBtn}
                      style={{ backgroundColor: '#0D9488', color: '#FFF' }}
                      onClick={() => history.push('/vendors?filter=' + encodeURIComponent(JSON.stringify({ pendingService: 'rental', rentalStatus: 'request_pending' })))}
                    >
                      Review
                    </Button>
                  </Box>
                </Box>
              </Grid>
            )}

            {/* Cash Payment Approvals */}
            {Object.values(cashPendingByService).map((svcGroup) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={svcGroup.serviceType}>
                <Box className={classes.approvalItem} style={{ borderLeft: '4px solid #E11D48' }}>
                  <Box>
                    <Box className={classes.approvalItemTop}>
                      <Box style={{ display: 'flex', alignItems: 'center' }}>
                        <Box className={classes.approvalIconBadge} style={{ backgroundColor: '#FFE4E6', color: '#E11D48' }}>
                          <PaymentIcon style={{ fontSize: 20 }} />
                        </Box>
                        <Box>
                          <span className={classes.approvalTypeBadge} style={{ backgroundColor: '#FFE4E6', color: '#BE123C' }}>
                            Cash Receipt
                          </span>
                          <Typography style={{ fontWeight: 800, fontSize: '0.85rem', color: '#1E293B', lineHeight: 1.2 }}>
                            {svcGroup.serviceTitle} Cash
                          </Typography>
                        </Box>
                      </Box>
                      <span className={classes.approvalCountBadge} style={{ backgroundColor: '#FFF1F2', color: '#E11D48', border: '1px solid #FECDD3' }}>
                        {svcGroup.count}
                      </span>
                    </Box>
                    <Typography style={{ fontSize: '0.725rem', color: '#64748B', marginTop: 6, lineHeight: 1.4 }}>
                      ₹{svcGroup.totalAmount.toLocaleString('en-IN')} cash collection awaiting administrative receipt verification.
                    </Typography>
                  </Box>
                  <Box className={classes.approvalItemBottom}>
                    <Typography style={{ fontSize: '0.7rem', fontWeight: 600, color: '#E11D48' }}>
                      ₹{svcGroup.totalAmount.toLocaleString('en-IN')} Total
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      className={classes.approvalActionBtn}
                      style={{ backgroundColor: '#E11D48', color: '#FFF' }}
                      onClick={() => history.push(`/${svcGroup.serviceType}requests`)}
                    >
                      Review
                    </Button>
                  </Box>
                </Box>
              </Grid>
            ))}

            {/* Courses Verification */}
            {pendingActions.pendingCourses > 0 && (
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Box className={classes.approvalItem} style={{ borderLeft: '4px solid #8B5CF6' }}>
                  <Box>
                    <Box className={classes.approvalItemTop}>
                      <Box style={{ display: 'flex', alignItems: 'center' }}>
                        <Box className={classes.approvalIconBadge} style={{ backgroundColor: '#F3E8FF', color: '#8B5CF6' }}>
                          <SchoolIcon style={{ fontSize: 20 }} />
                        </Box>
                        <Box>
                          <span className={classes.approvalTypeBadge} style={{ backgroundColor: '#F3E8FF', color: '#7C3AED' }}>
                            Curriculum
                          </span>
                          <Typography style={{ fontWeight: 800, fontSize: '0.85rem', color: '#1E293B', lineHeight: 1.2 }}>
                            Courses Pending
                          </Typography>
                        </Box>
                      </Box>
                      <span className={classes.approvalCountBadge} style={{ backgroundColor: '#F5F3FF', color: '#8B5CF6', border: '1px solid #DDD6FE' }}>
                        {pendingActions.pendingCourses}
                      </span>
                    </Box>
                    <Typography style={{ fontSize: '0.725rem', color: '#64748B', marginTop: 6, lineHeight: 1.4 }}>
                      Vendor-submitted course curriculums awaiting content & pricing verification.
                    </Typography>
                  </Box>
                  <Box className={classes.approvalItemBottom}>
                    <Typography style={{ fontSize: '0.7rem', fontWeight: 600, color: '#8B5CF6' }}>
                      {pendingActions.pendingCourses} New Courses
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      className={classes.approvalActionBtn}
                      style={{ backgroundColor: '#8B5CF6', color: '#FFF' }}
                      onClick={() => history.push('/course?filter=' + encodeURIComponent(JSON.stringify({ isAdminApproved: false })))}
                    >
                      Verify
                    </Button>
                  </Box>
                </Box>
              </Grid>
            )}

            {/* Tools / Rental Listings */}
            {pendingActions.pendingTools > 0 && (
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Box className={classes.approvalItem} style={{ borderLeft: '4px solid #F59E0B' }}>
                  <Box>
                    <Box className={classes.approvalItemTop}>
                      <Box style={{ display: 'flex', alignItems: 'center' }}>
                        <Box className={classes.approvalIconBadge} style={{ backgroundColor: '#FEF3C7', color: '#F59E0B' }}>
                          <BuildIcon style={{ fontSize: 20 }} />
                        </Box>
                        <Box>
                          <span className={classes.approvalTypeBadge} style={{ backgroundColor: '#FEF3C7', color: '#B45309' }}>
                            Listing
                          </span>
                          <Typography style={{ fontWeight: 800, fontSize: '0.85rem', color: '#1E293B', lineHeight: 1.2 }}>
                            Equipment Listings
                          </Typography>
                        </Box>
                      </Box>
                      <span className={classes.approvalCountBadge} style={{ backgroundColor: '#FFFBEB', color: '#F59E0B', border: '1px solid #FDE68A' }}>
                        {pendingActions.pendingTools}
                      </span>
                    </Box>
                    <Typography style={{ fontSize: '0.725rem', color: '#64748B', marginTop: 6, lineHeight: 1.4 }}>
                      Machinery and tool rental items submitted by vendors for listing approval.
                    </Typography>
                  </Box>
                  <Box className={classes.approvalItemBottom}>
                    <Typography style={{ fontSize: '0.7rem', fontWeight: 600, color: '#F59E0B' }}>
                      {pendingActions.pendingTools} Listings
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      className={classes.approvalActionBtn}
                      style={{ backgroundColor: '#F59E0B', color: '#FFF' }}
                      onClick={() => history.push('/tools')}
                    >
                      Verify
                    </Button>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {/* 4. Service Performance Matrix */}
      <Typography className={classes.sectionTitle}>
        <WorkIcon style={{ color: '#0284C7', fontSize: 18 }} /> Service Performance Matrix
      </Typography>
      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        {/* Manpower */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.serviceCard} style={{ borderTop: '4px solid #0284C7' }}>
            <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Typography style={{ fontWeight: 800, color: '#0F172A', fontSize: '0.9rem' }}>Manpower</Typography>
              {loading && <CircularProgress size={14} style={{ color: '#0284C7' }} />}
            </Box>
            <Typography style={{ fontSize: '0.75rem', color: '#64748B' }}>
              Requests: <b>{servicePerf.manpower.totalRequests}</b> | Active: <b>{servicePerf.manpower.activeJobs}</b>
            </Typography>

            <Divider style={{ margin: '8px 0' }} />
            <Box style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: 3 }}>
              <span>GMV:</span>
              <strong style={{ color: '#0284C7' }}>₹{fmtVal(getVal(servicePerf.manpower, 'gmv', 'acceptedQuotationValue'))}</strong>
            </Box>
            <Box style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: 3 }}>
              <span>Collected:</span>
              <strong style={{ color: '#16A34A' }}>₹{fmtVal(getVal(servicePerf.manpower, 'paymentsReceived', 'confirmedCustomerCollections'))}</strong>
            </Box>
            <Box style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: 3 }}>
              <span>Outstanding:</span>
              <strong style={{ color: '#B45309' }}>₹{fmtVal(getVal(servicePerf.manpower, 'outstandingBalance', 'outstandingCustomerBalances'))}</strong>
            </Box>
            <Box style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem' }}>
              <span>Platform Comm:</span>
              <strong style={{ color: '#4F46E5' }}>₹{fmtVal(getVal(servicePerf.manpower, 'platformRevenue'))}</strong>
            </Box>

            <Button
              fullWidth
              size="small"
              variant="outlined"
              style={{ marginTop: 10, textTransform: 'none', fontSize: '0.725rem', padding: '3px 6px', borderColor: '#0284C7', color: '#0284C7' }}
              onClick={() => history.push('/manpowerrequests')}
            >
              View Manpower
            </Button>
          </Paper>
        </Grid>

        {/* Project */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.serviceCard} style={{ borderTop: '4px solid #D97706' }}>
            <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Typography style={{ fontWeight: 800, color: '#0F172A', fontSize: '0.9rem' }}>Project</Typography>
              {loading && <CircularProgress size={14} style={{ color: '#D97706' }} />}
            </Box>
            <Typography style={{ fontSize: '0.75rem', color: '#64748B' }}>
              Requests: <b>{servicePerf.project.totalRequests}</b> | Active: <b>{servicePerf.project.activeJobs}</b>
            </Typography>

            <Divider style={{ margin: '8px 0' }} />
            <Box style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: 3 }}>
              <span>GMV:</span>
              <strong style={{ color: '#0284C7' }}>₹{fmtVal(getVal(servicePerf.project, 'gmv', 'acceptedQuotationValue'))}</strong>
            </Box>
            <Box style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: 3 }}>
              <span>Collected:</span>
              <strong style={{ color: '#16A34A' }}>₹{fmtVal(getVal(servicePerf.project, 'paymentsReceived', 'confirmedCustomerCollections'))}</strong>
            </Box>
            <Box style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: 3 }}>
              <span>Outstanding:</span>
              <strong style={{ color: '#B45309' }}>₹{fmtVal(getVal(servicePerf.project, 'outstandingBalance', 'outstandingCustomerBalances'))}</strong>
            </Box>
            <Box style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem' }}>
              <span>Platform Comm:</span>
              <strong style={{ color: '#4F46E5' }}>₹{fmtVal(getVal(servicePerf.project, 'platformRevenue'))}</strong>
            </Box>

            <Button
              fullWidth
              size="small"
              variant="outlined"
              style={{ marginTop: 10, textTransform: 'none', fontSize: '0.725rem', padding: '3px 6px', borderColor: '#D97706', color: '#D97706' }}
              onClick={() => history.push('/projectrequests')}
            >
              View Project
            </Button>
          </Paper>
        </Grid>

        {/* Training */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.serviceCard} style={{ borderTop: '4px solid #7C3AED' }}>
            <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Typography style={{ fontWeight: 800, color: '#0F172A', fontSize: '0.9rem' }}>Training</Typography>
              {loading && <CircularProgress size={14} style={{ color: '#7C3AED' }} />}
            </Box>
            <Typography style={{ fontSize: '0.75rem', color: '#64748B' }}>
              Requests: <b>{servicePerf.training.totalRequests}</b> | Courses: <b>{servicePerf.training.approvedCourses || 0}</b>
            </Typography>

            <Divider style={{ margin: '8px 0' }} />
            <Box style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: 3 }}>
              <span>GMV:</span>
              <strong style={{ color: '#0284C7' }}>₹{fmtVal(getVal(servicePerf.training, 'gmv', 'acceptedQuotationValue'))}</strong>
            </Box>
            <Box style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: 3 }}>
              <span>Collected:</span>
              <strong style={{ color: '#16A34A' }}>₹{fmtVal(getVal(servicePerf.training, 'paymentsReceived', 'confirmedCustomerCollections'))}</strong>
            </Box>
            <Box style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: 3 }}>
              <span>Outstanding:</span>
              <strong style={{ color: '#B45309' }}>₹{fmtVal(getVal(servicePerf.training, 'outstandingBalance', 'outstandingCustomerBalances'))}</strong>
            </Box>
            <Box style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: 3 }}>
              <span>Platform Comm:</span>
              <strong style={{ color: '#4F46E5' }}>₹{fmtVal(getVal(servicePerf.training, 'platformRevenue'))}</strong>
            </Box>
            {/* <Box style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem' }}>
              <span>Pending Trainers:</span>
              <strong style={{ color: (servicePerf.training.pendingTrainers || 0) > 0 ? '#DC2626' : '#16A34A' }}>
                {servicePerf.training.pendingTrainers || 0}
              </strong>
            </Box> */}

            <Box style={{ display: 'flex', gap: 6, marginTop: 10 }}>
              <Button
                fullWidth
                size="small"
                variant="outlined"
                style={{ textTransform: 'none', fontSize: '0.725rem', padding: '3px 6px', borderColor: '#7C3AED', color: '#7C3AED' }}
                onClick={() => history.push('/trainingrequests')}
              >
                View Training
              </Button>
              {(servicePerf.training.pendingTrainers || 0) > 0 && (
                <Button
                  fullWidth
                  size="small"
                  variant="contained"
                  style={{ textTransform: 'none', fontSize: '0.725rem', padding: '3px 6px', backgroundColor: '#7C3AED', color: '#FFF' }}
                  onClick={() => history.push('/vendors?filter=' + encodeURIComponent(JSON.stringify({ trainerStatus: 'request_pending' })))}
                >
                  Trainers ({servicePerf.training.pendingTrainers})
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Rental */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.serviceCard} style={{ borderTop: '4px solid #0D9488' }}>
            <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Typography style={{ fontWeight: 800, color: '#0F172A', fontSize: '0.9rem' }}>Rental</Typography>
              {loading && <CircularProgress size={14} style={{ color: '#0D9488' }} />}
            </Box>
            <Typography style={{ fontSize: '0.75rem', color: '#64748B' }}>
              Requests: <b>{servicePerf.rental.totalRequests}</b> | Listings: <b>{servicePerf.rental.approvedListings || 0}</b>
            </Typography>

            <Divider style={{ margin: '8px 0' }} />
            <Box style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: 3 }}>
              <span>GMV:</span>
              <strong style={{ color: '#0284C7' }}>₹{fmtVal(getVal(servicePerf.rental, 'gmv', 'acceptedQuotationValue'))}</strong>
            </Box>
            <Box style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: 3 }}>
              <span>Collected:</span>
              <strong style={{ color: '#16A34A' }}>₹{fmtVal(getVal(servicePerf.rental, 'paymentsReceived', 'confirmedCustomerCollections'))}</strong>
            </Box>
            <Box style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: 3 }}>
              <span>Outstanding:</span>
              <strong style={{ color: '#B45309' }}>₹{fmtVal(getVal(servicePerf.rental, 'outstandingBalance', 'outstandingCustomerBalances'))}</strong>
            </Box>
            <Box style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem' }}>
              <span>Platform Comm:</span>
              <strong style={{ color: '#4F46E5' }}>₹{fmtVal(getVal(servicePerf.rental, 'platformRevenue'))}</strong>
            </Box>

            <Button
              fullWidth
              size="small"
              variant="outlined"
              style={{ marginTop: 10, textTransform: 'none', fontSize: '0.725rem', padding: '3px 6px', borderColor: '#0D9488', color: '#0D9488' }}
              onClick={() => history.push('/rentalrequests')}
            >
              View Rental
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* 5. Financial Performance & Reworked Financial Trend Graph (Explicit Height Bars) */}
      <Grid container spacing={2} alignItems="stretch" style={{ marginBottom: 16 }}>
        <Grid item xs={12} md={8}>
          <Paper className={classes.chartContainer} style={{ height: '100%' }}>
            <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <Typography className={classes.sectionTitle} style={{ marginBottom: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                <TrendingUpIcon style={{ color: '#4F46E5', fontSize: 18 }} /> Financial Performance & Revenue Trend
                {loading && <CircularProgress size={14} style={{ color: '#4F46E5', marginLeft: 4 }} />}
              </Typography>

              {/* Legend */}
              <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 10, height: 10, backgroundColor: '#0284C7', borderRadius: 2, display: 'inline-block' }} />
                  <Typography style={{ fontSize: '0.7rem', color: '#475569', fontWeight: 600 }}>GMV (₹)</Typography>
                </Box>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 10, height: 10, backgroundColor: '#6366F1', borderRadius: 2, display: 'inline-block' }} />
                  <Typography style={{ fontSize: '0.7rem', color: '#475569', fontWeight: 600 }}>Revenue (₹)</Typography>
                </Box>
              </Box>
            </Box>

            {/* Visual Bar Chart with Explicit Pixel Heights */}
            <Box className={classes.chartBarArea} style={{ display: 'flex', alignItems: 'flex-end', height: 140, justifyContent: 'space-around', paddingTop: 12, paddingBottom: 6, borderBottom: '1px solid #E2E8F0' }}>
              {revenueTrend.length > 0 ? (
                revenueTrend.map((t, idx) => {
                  const gmvPx = (t.gmv && maxTrendVal > 0) ? Math.max(14, Math.round((t.gmv / maxTrendVal) * 85)) : (t.gmv === 0 ? 6 : 14);
                  const revPx = (t.revenue && maxTrendVal > 0) ? Math.max(10, Math.round((t.revenue / maxTrendVal) * 90)) : (t.revenue === 0 ? 6 : 10);
                  const dateParts = (t.date || '').split('-');
                  const formattedDate = dateParts.length === 3 ? `${dateParts[2]}/${dateParts[1]}` : t.date;

                  return (
                    <Tooltip
                      key={idx}
                      title={`${t.date}: GMV ₹${(t.gmv || 0).toLocaleString('en-IN')} | Revenue ₹${(t.revenue || 0).toLocaleString('en-IN')}`}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', minWidth: 44, height: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 95, width: '100%', justifyContent: 'center' }}>
                          <div style={{ width: 14, minWidth: 14, backgroundColor: '#0284C7', borderRadius: '3px 3px 0 0', height: `${gmvPx}px`, minHeight: `${gmvPx}px` }} />
                          <div style={{ width: 14, minWidth: 14, backgroundColor: '#6366F1', borderRadius: '3px 3px 0 0', height: `${revPx}px`, minHeight: `${revPx}px` }} />
                        </div>
                        <span className={classes.chartDateLabel}>{formattedDate}</span>
                      </div>
                    </Tooltip>
                  );
                })
              ) : (
                <Typography style={{ color: '#94A3B8', fontSize: '0.8rem', margin: 'auto' }}>
                  No transaction trend data available for selected date range
                </Typography>
              )}
            </Box>

            <Box style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, paddingTop: 4 }}>
              <Typography style={{ fontSize: '0.75rem', color: '#475569' }}>
                Total Agreed GMV: <b style={{ color: '#0284C7' }}>₹{financials.gmv.toLocaleString('en-IN')}</b>
              </Typography>
              <Typography style={{ fontSize: '0.75rem', color: '#475569' }}>
                Platform Net Revenue: <b style={{ color: '#4F46E5' }}>₹{financials.platformRevenue.toLocaleString('en-IN')}</b>
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Payment Method Breakdown */}
        <Grid item xs={12} md={4}>
          <Paper className={classes.chartContainer} style={{ height: '100%' }}>
            <Typography className={classes.sectionTitle} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <PaymentIcon style={{ color: '#059669', fontSize: 18 }} /> Payment Method Breakdown
              {loading && <CircularProgress size={14} style={{ color: '#059669', marginLeft: 4 }} />}
            </Typography>
            <Box style={{ marginTop: 12 }}>
              <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, padding: '8px 12px', backgroundColor: '#F0FDF4', borderRadius: 6, border: '1px solid #DCFCE7' }}>
                <Typography style={{ fontSize: '0.8rem', color: '#166534', fontWeight: 700 }}>Online Confirmed</Typography>
                <Typography style={{ fontSize: '0.85rem', fontWeight: 800, color: '#166534' }}>
                  ₹{(dashData?.paymentMethodBreakdown?.onlinePaymentAmount || 0).toLocaleString('en-IN')}
                </Typography>
              </Box>

              <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, padding: '8px 12px', backgroundColor: '#ECFDF5', borderRadius: 6, border: '1px solid #A7F3D0' }}>
                <Typography style={{ fontSize: '0.8rem', color: '#047857', fontWeight: 700 }}>Approved Cash</Typography>
                <Typography style={{ fontSize: '0.85rem', fontWeight: 800, color: '#047857' }}>
                  ₹{(dashData?.paymentMethodBreakdown?.cashApprovedAmount || 0).toLocaleString('en-IN')}
                </Typography>
              </Box>

              <Box style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', backgroundColor: '#FFF7ED', borderRadius: 6, border: '1px solid #FFEDD5' }}>
                <Typography style={{ fontSize: '0.8rem', color: '#C2410C', fontWeight: 700 }}>Pending Cash Receipt</Typography>
                <Typography style={{ fontSize: '0.85rem', fontWeight: 800, color: '#C2410C' }}>
                  ₹{financials.pendingCashAmount.toLocaleString('en-IN')}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* 6. Recent Activity & Transaction Data Tables */}
      <Paper className={classes.tablePaper}>
        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #E2E8F0', paddingRight: 12 }}>
          <Tabs
            value={activeTab}
            onChange={(e, val) => setActiveTab(val)}
            indicatorColor="primary"
            textColor="primary"
            style={{ minHeight: 40 }}
          >
            <Tab label="Recent Job Requests" style={{ textTransform: 'none', fontSize: '0.8rem', fontWeight: 700, minHeight: 40 }} />
            <Tab label="Recent Client Transactions" style={{ textTransform: 'none', fontSize: '0.8rem', fontWeight: 700, minHeight: 40 }} />
            <Tab label="Pending Cash Receipts" style={{ textTransform: 'none', fontSize: '0.8rem', fontWeight: 700, minHeight: 40 }} />
          </Tabs>
          {loading && <CircularProgress size={16} style={{ color: '#0284C7' }} />}
        </Box>

        {/* Tab 0: Recent Requests */}
        {activeTab === 0 && (
          <Box className={classes.tableContainerResponsive}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableHeaderCell}>JOB ID</TableCell>
                  <TableCell className={classes.tableHeaderCell}>SERVICE</TableCell>
                  <TableCell className={classes.tableHeaderCell}>CUSTOMER</TableCell>
                  <TableCell className={classes.tableHeaderCell}>APPROVED VENDOR ID</TableCell>
                  <TableCell className={classes.tableHeaderCell}>AGREED AMOUNT</TableCell>
                  <TableCell className={classes.tableHeaderCell}>AMOUNT PAID</TableCell>
                  <TableCell className={classes.tableHeaderCell}>STATUS</TableCell>
                  <TableCell className={classes.tableHeaderCell}>ACTION</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dashData?.recentRequests?.length > 0 ? (
                  dashData.recentRequests.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell className={classes.tableCell} style={{ fontWeight: 800, fontFamily: 'monospace' }}>
                        {(r.jobId || String(r.id)).toUpperCase()}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <Chip label={(r.serviceTitle || '').toUpperCase()} size="small" style={{ fontSize: '0.65rem', fontWeight: 700, height: 20, backgroundColor: '#EEF2FF', color: '#4338CA' }} />
                      </TableCell>
                      <TableCell className={classes.tableCell}>{r.customerName}</TableCell>
                      <TableCell className={classes.tableCell} style={{ fontFamily: 'monospace', fontWeight: 700 }}>
                        {(r.approvedVendorId || '-').toUpperCase()}
                      </TableCell>
                      <TableCell className={classes.tableCell} style={{ fontWeight: 700 }}>
                        ₹{r.amountToBePaid.toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell className={classes.tableCell} style={{ color: '#16A34A', fontWeight: 700 }}>
                        ₹{r.amountPaid.toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {getStatusBadge(r.status)}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <Tooltip title="View Request Details">
                          <IconButton
                            size="small"
                            style={{ color: '#0284C7', backgroundColor: '#F0F9FF', padding: 5 }}
                            onClick={() => history.push(`/${r.serviceType}requests/${r.id}`)}
                          >
                            <VisibilityIcon style={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center" style={{ padding: 16, color: '#94A3B8', fontSize: '0.8rem' }}>
                      No recent job requests found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* Tab 1: Recent Transactions */}
        {activeTab === 1 && (
          <Box className={classes.tableContainerResponsive}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableHeaderCell}>TX ID</TableCell>
                  <TableCell className={classes.tableHeaderCell}>JOB ID</TableCell>
                  <TableCell className={classes.tableHeaderCell}>PAYMENT MODE</TableCell>
                  <TableCell className={classes.tableHeaderCell}>AMOUNT RECEIVED</TableCell>
                  <TableCell className={classes.tableHeaderCell}>PLATFORM COMM.</TableCell>
                  <TableCell className={classes.tableHeaderCell}>VENDOR EARNING</TableCell>
                  <TableCell className={classes.tableHeaderCell}>ACTION</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dashData?.recentPayments?.length > 0 ? (
                  dashData.recentPayments.map((tx) => (
                    <TableRow key={tx.id || tx._id} hover>
                      <TableCell className={classes.tableCell} style={{ fontWeight: 800 }}>
                        {String(tx.id || tx._id).toUpperCase()}
                      </TableCell>
                      <TableCell className={classes.tableCell} style={{ fontWeight: 700, fontFamily: 'monospace' }}>
                        {String(tx.jobId || '').toUpperCase()}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <Chip label={String(tx.modeOfPayment || '').toUpperCase()} size="small" style={{ backgroundColor: '#E0F2FE', color: '#0369A1', fontSize: '0.65rem', fontWeight: 800, height: 20 }} />
                      </TableCell>
                      <TableCell className={classes.tableCell} style={{ fontWeight: 700, color: '#16A34A' }}>
                        ₹{(parseFloat(tx.amountReceived) || 0).toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell className={classes.tableCell} style={{ fontWeight: 700, color: '#4F46E5' }}>
                        ₹{(parseFloat(tx.commissionAmount) || 0).toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell className={classes.tableCell} style={{ fontWeight: 700 }}>
                        ₹{(parseFloat(tx.vendorEarning) || 0).toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <Tooltip title="View Transaction Details">
                          <IconButton
                            size="small"
                            style={{ color: '#0284C7', backgroundColor: '#F0F9FF', padding: 5 }}
                            onClick={() => history.push('/accounts')}
                          >
                            <VisibilityIcon style={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" style={{ padding: 16, color: '#94A3B8', fontSize: '0.8rem' }}>
                      No recent transaction records found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* Tab 2: Pending Cash Receipts */}
        {activeTab === 2 && (
          <Box className={classes.tableContainerResponsive}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableHeaderCell}>JOB ID</TableCell>
                  <TableCell className={classes.tableHeaderCell}>SERVICE</TableCell>
                  <TableCell className={classes.tableHeaderCell}>CUSTOMER</TableCell>
                  <TableCell className={classes.tableHeaderCell}>PENDING CASH AMOUNT</TableCell>
                  <TableCell className={classes.tableHeaderCell}>REQUEST DATE</TableCell>
                  <TableCell className={classes.tableHeaderCell}>ACTION</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dashData?.pendingCashApprovalsList?.length > 0 ? (
                  dashData.pendingCashApprovalsList.map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell className={classes.tableCell} style={{ fontWeight: 800, fontFamily: 'monospace' }}>
                        {String(p.jobId || p.id).toUpperCase()}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <Chip label={(p.serviceTitle || '').toUpperCase()} size="small" style={{ fontSize: '0.65rem', fontWeight: 700, height: 20 }} />
                      </TableCell>
                      <TableCell className={classes.tableCell}>{p.customerName}</TableCell>
                      <TableCell className={classes.tableCell} style={{ fontWeight: 800, color: '#DC2626' }}>
                        ₹{p.amountRequested.toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {p.createdAt ? new Date(p.createdAt).toLocaleDateString('en-IN') : '-'}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <Tooltip title="Approve or Review Cash Receipt">
                          <IconButton
                            size="small"
                            style={{ color: '#D97706', backgroundColor: '#FEF3C7', padding: 5 }}
                            onClick={() => history.push(`/${p.serviceType}requests/${p.id}`)}
                          >
                            <VisibilityIcon style={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" style={{ padding: 16, color: '#16A34A', fontWeight: 700, fontSize: '0.8rem' }}>
                      All cash receipts are approved! No pending cash approvals.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>
    </div>
  );
};

export default withStyles(styles)(withDataProvider(withRouter(AdminDashboard)));
